import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Skeleton,
} from '@mui/material';
import {
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/solid';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency, CURRENCY } from '../../config/currency';

// --- Types ---

export interface CashMovement {
  id: number;
  direction: 'IN' | 'OUT';
  amount: number;
  source: string;
  description?: string | null;
  time: string; // ISO-8601 timestamp from backend CashMovementEntry.time
  type?: string; // e.g. 'SALE', 'PETTY_CASH', 'MANUAL', 'EXPENSE'
}

export interface CashMovementListProps {
  movements: CashMovement[];
  loading?: boolean;
}

// --- Helpers ---

const formatTime = (dateStr: string) => {
  try {
    return new Date(dateStr).toLocaleTimeString(CURRENCY.locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return dateStr;
  }
};

const groupByHour = (movements: CashMovement[]): Map<string, CashMovement[]> => {
  const groups = new Map<string, CashMovement[]>();
  // Sort newest first
  const sorted = [...movements].sort(
    (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()
  );
  sorted.forEach((m) => {
    try {
      const date = new Date(m.time);
      const hourKey = date.toLocaleTimeString(CURRENCY.locale, { hour: '2-digit', hour12: true });
      const existing = groups.get(hourKey) || [];
      existing.push(m);
      groups.set(hourKey, existing);
    } catch {
      const existing = groups.get('Other') || [];
      existing.push(m);
      groups.set('Other', existing);
    }
  });
  return groups;
};

// --- Component ---

const CashMovementList: React.FC<CashMovementListProps> = ({
  movements,
  loading = false,
}) => {
  const { theme } = useTheme();

  // --- Skeleton loader ---
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Paper
            key={i}
            sx={{
              p: 2,
              borderRadius: '10px',
              backgroundColor: theme.components?.card?.background || '#FFFFFF',
              border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
            }}
            elevation={0}
          >
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Skeleton
                variant="circular"
                width={36}
                height={36}
                sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0', flexShrink: 0 }}
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
                <Skeleton variant="text" width="40%" sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
              </Box>
              <Skeleton
                variant="rounded"
                width={70}
                height={24}
                sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }}
              />
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  // --- Empty state ---
  if (movements.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '2rem', mb: 1 }}>{'\uD83D\uDCB0'}</Typography>
        <Typography variant="body1" sx={{ fontWeight: 600, color: theme.text, mb: 0.5 }}>
          No cash movements today
        </Typography>
        <Typography variant="body2" sx={{ color: theme.gray?.[500] || '#666' }}>
          Record cash in or out to track your register
        </Typography>
      </Box>
    );
  }

  // --- Grouped timeline ---
  const groups = groupByHour(movements);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {Array.from(groups.entries()).map(([hourLabel, items]) => (
        <Box key={hourLabel}>
          {/* Hour group label */}
          <Typography
            variant="caption"
            sx={{
              color: theme.gray?.[500] || '#666',
              fontWeight: 600,
              textTransform: 'uppercase',
              fontSize: '0.65rem',
              letterSpacing: '0.05em',
              display: 'block',
              mb: 0.5,
              mt: 1,
              ml: 0.5,
            }}
          >
            {hourLabel}
          </Typography>

          {items.map((movement) => {
            const isIn = movement.direction === 'IN';
            const dirColor = isIn ? (theme.success || '#4CAF50') : (theme.danger || '#D99982');

            return (
              <Paper
                key={movement.id}
                data-testid={`cash-movement-${movement.id}`}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  mb: 1,
                  borderRadius: '10px',
                  backgroundColor: theme.components?.card?.background || '#FFFFFF',
                  border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
                  transition: 'all 0.15s ease',
                  '&:hover': { borderColor: `${theme.primary}66` },
                }}
                elevation={0}
              >
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  {/* Direction icon */}
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      backgroundColor: `${dirColor}15`,
                      border: `1.5px solid ${dirColor}44`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {isIn ? (
                      <ArrowUpIcon style={{ width: 18, height: 18, color: dirColor }} />
                    ) : (
                      <ArrowDownIcon style={{ width: 18, height: 18, color: dirColor }} />
                    )}
                  </Box>

                  {/* Details */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ fontWeight: 600, color: theme.text, fontSize: '0.875rem' }}
                      >
                        {movement.source}
                      </Typography>
                      {movement.type && (
                        <Chip
                          label={movement.type.replace(/_/g, ' ')}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.6rem',
                            height: 18,
                            backgroundColor: theme.gray?.[100] || '#F0F0F0',
                            color: theme.gray?.[500] || '#666',
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography data-testid={`movement-time-${movement.id}`} variant="caption" sx={{ color: theme.gray?.[500] || '#666' }}>
                        {formatTime(movement.time)}
                      </Typography>
                      {movement.description && (
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{ color: theme.gray?.[400] || '#999', fontStyle: 'italic' }}
                        >
                          {movement.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Amount */}
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: dirColor,
                      fontSize: { xs: '0.95rem', sm: '1.05rem' },
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isIn ? '+' : '-'}{formatCurrency(movement.amount, { showDecimals: false })}
                  </Typography>
                </Box>
              </Paper>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

export default CashMovementList;
